import { Component, DestroyRef, inject, Input, OnChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { MessageService } from '../../../../services/message/message.service';
import { GroupAudienceService, GroupRef, Reach } from '../../../../services/segments/group-audience.service';
import { IContact, IOrganization } from '../../../../types/contactModule';
import {
  GroupContactsTableComponent,
  TMemberAction,
} from '../../../../components/tables/group-contacts-table/group-contacts-table.component';
import { AssignContactsToGroupComponent } from '../../../../components/modals/assign-contacts-to-group/assign-contacts-to-group.component';
import { EditOrganizationFormComponent } from '../../../../components/forms/edit-organization-form/edit-organization-form.component';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { BackButtonComponent } from '../../../../components/ui/back-button/back-button.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { makeArrayUnique } from '../../../../../utils/utils';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { countNoun, humanizeEnum } from '../../../../components/tables/group-table/group-rows';
import { contactName } from '../../../../components/tables/contact-table/contact-row';

@Component({
  selector: 'app-organization-details',
  standalone: true,
  imports: [
    GroupContactsTableComponent,
    AssignContactsToGroupComponent,
    EditOrganizationFormComponent,
    PromptCardComponent,
    BackButtonComponent,
    HasPermissionDirective,
  ],
  templateUrl: './organization-details.component.html',
  styleUrls: ['../../../../components/tables/group-table/group-details.css', './organization-details.component.css'],
})
export class OrganizationDetailsComponent implements OnChanges {
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);
  private audience = inject(GroupAudienceService);
  private messageService = inject(MessageService);
  router = inject(Router);
  contactservice = inject(ContactsService);

  @ViewChild(GroupContactsTableComponent) membersTable?: GroupContactsTableComponent;

  /** The organization's uuid, from the route. */
  @Input() id!: string;

  readonly formatCount = formatCount;

  organizationDetail: IOrganization | null = null;
  organizationId: string | number = '';
  loading = true;
  loadError = '';
  private loadSub?: Subscription;
  private countSub?: Subscription;

  contactsCount: number | null = null;
  /** The member count did not load. */
  countError = false;
  reach: Reach | null = null;
  selectedContacts: IContact[] = [];

  showAddAnotherContact: boolean = false;
  addingContactsToOrganization: boolean = false;
  showEditModal = false;

  // removal of contacts from the organization
  showRemoveContactsPrompt: boolean = false;
  removing = false;
  /** Contacts the remove prompt is about: the ticked ones, or one row. */
  contactsToRemove: IContact[] = [];
  /** The prompt came from one row's Remove, not the selection bar. */
  private removingOneRow = false;

  get ref(): GroupRef | null {
    const org = this.organizationDetail;
    return org?.uuid ? { kind: 'organization', uuid: org.uuid, id: org.id ?? null } : null;
  }

  get name(): string {
    return (this.organizationDetail?.name ?? '').toString().trim() || 'Untitled organization';
  }

  get description(): string {
    return (this.organizationDetail?.description ?? '').toString().trim();
  }

  get industry(): string {
    return humanizeEnum(this.organizationDetail?.industry) || '—';
  }

  get place(): string {
    const org = this.organizationDetail;
    return [org?.city, org?.region, org?.country].map((part) => (part ?? '').toString().trim()).filter(Boolean).join(', ') || '—';
  }

  get created(): string {
    return formatWhen(this.organizationDetail?.createdAt);
  }

  get updated(): string {
    return formatWhen(this.organizationDetail?.modifiedAt || this.organizationDetail?.createdAt);
  }

  get removeTitle(): string {
    const count = this.contactsToRemove.length;
    if (count === 1) return `Remove ${contactName(this.contactsToRemove[0]) || 'this contact'} from ${this.name}?`;
    return `Remove ${count} contacts from ${this.name}?`;
  }

  get removeButton(): string {
    return this.contactsToRemove.length === 1 ? 'Remove contact' : `Remove ${this.contactsToRemove.length} contacts`;
  }

  // ── Loading ──
  loadOrganization() {
    if (!this.id) return;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .getOrganization(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (organization) => {
          this.organizationDetail = organization;
          this.organizationId = organization?.uuid ?? '';
          this.loading = false;
          this.handleFetchOrganizationContactsCount();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'This organization did not load. It may have been deleted, or the connection dropped.';
        },
      });
  }

  /** Member count, then how many members have no phone number. */
  handleFetchOrganizationContactsCount() {
    const ref = this.ref;
    if (!ref) return;
    this.countError = false;
    // A newer count replaces one still in flight, so a slow, older reply never lands last.
    this.countSub?.unsubscribe();
    this.countSub = this.audience
      .memberCount(ref)
      .pipe(
        switchMap((count) => {
          this.contactsCount = count;
          return this.audience.reach(ref, count);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (reach) => (this.reach = reach),
        error: () => {
          this.contactsCount = null;
          this.countError = true;
        },
      });
  }

  /** After members change: fresh count and a fresh members page. */
  private membersChanged() {
    this.audience.invalidate(this.ref);
    this.handleFetchOrganizationContactsCount();
  }

  onMembersChanged() {
    this.membersChanged();
  }

  // ── Header actions ──
  goBack() {
    this.location.back(); // Navigates back in the platform's history
  }

  /** Opens the composer with this organization already picked. */
  messageGroup() {
    if (!this.organizationDetail) return;
    this.messageService.updateSelectedOrganizations([this.organizationDetail]);
    this.router.navigate(['/organization/messages/create']);
  }

  openEdit() {
    this.showEditModal = true;
  }

  closeEdit() {
    this.showEditModal = false;
    // The modal saves through ContactsService; read the record back.
    this.loadOrganization();
  }

  openShowAddAnotherContact() {
    this.showAddAnotherContact = true;
  }
  closeShowAddAnotherContact() {
    this.showAddAnotherContact = false;
  }

  handleAssignContactsToOrganization(selectedContacts: IContact[]) {
    if (!selectedContacts?.length || !this.organizationDetail?.uuid) return;
    this.addingContactsToOrganization = true;
    this.contactservice
      .assignMultipleContactsToOrganizations(
        selectedContacts.map((el) => el.uuid as string),
        [this.organizationDetail.uuid as string]
      )
      .subscribe({
        next: () => {
          this.addingContactsToOrganization = false;
          this.closeShowAddAnotherContact();
          this.membersChanged();
          this.membersTable?.refresh();
        },
        error: () => {
          this.addingContactsToOrganization = false;
          this.contactservice.showError('The contacts were not added. Try again.');
        },
      });
  }

  // ── Members ──
  updateSelectedContacts(contacts: IContact[]) {
    this.selectedContacts = contacts;
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
    this.membersTable?.unselectAll();
  }

  get getSelectedContacts() {
    return makeArrayUnique(this.selectedContacts);
  }

  handleAction(action: TMemberAction | string) {
    if (this.selectedContacts.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'remove':
        this.openRemoveContactsPrompt();
        break;
    }
  }

  sendSMStoSelectedContacts() {
    const params = { items: JSON.stringify(this.selectedContacts) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
    this.resetSelectedContacts();
  }

  openRemoveContactsPrompt(contacts?: IContact[]) {
    this.removingOneRow = !!contacts?.length;
    this.contactsToRemove = contacts?.length ? contacts : this.getSelectedContacts;
    if (this.contactsToRemove.length) this.showRemoveContactsPrompt = true;
  }

  removeOne(contact: IContact) {
    this.openRemoveContactsPrompt([contact]);
  }

  closeRemoveContactsPrompt() {
    if (this.removing) return;
    this.showRemoveContactsPrompt = false;
  }

  removeSelectedContactsFromOrganization() {
    const uuid = this.organizationDetail?.uuid;
    const contacts = this.contactsToRemove;
    if (!uuid || !contacts.length || this.removing) return;
    this.removing = true;
    this.contactservice
      .removeOrganizationContacts(
        uuid,
        contacts.map((el) => el.uuid as string)
      )
      .subscribe({
        next: () => {
          this.removing = false;
          this.showRemoveContactsPrompt = false;
          // A row's Remove leaves the other ticked members ticked.
          if (this.removingOneRow) {
            this.membersTable?.deselect(contacts);
          } else {
            this.resetSelectedContacts();
          }
          this.membersChanged();
          this.membersTable?.load();
          this.contactservice.showSuccess(
            contacts.length === 1 ? 'Contact removed from the organization.' : `${contacts.length} contacts removed from the organization.`
          );
        },
        error: () => {
          this.removing = false;
          this.showRemoveContactsPrompt = false;
          this.contactservice.showError('The contacts were not removed. Try again.');
        },
      });
  }

  countNoun = countNoun;

  ngOnChanges() {
    // The route reuses this component when moving between organizations.
    this.organizationDetail = null;
    this.countSub?.unsubscribe();
    this.contactsCount = null;
    this.reach = null;
    this.selectedContacts = [];
    this.loadOrganization();
  }

  ngOnInit() {
    if (!this.loadSub) this.loadOrganization();
  }
}
